import PropTypes from 'prop-types';
import React from 'react';
import SplitLayout from '../../common/layouts/splitLayout';
import AudioContainer from '../../common/audio/audioContainer';
import AnswerButtonContainer from '../../common/splitPageQuestion/AnswerButton/AnswerButtonContainer';
import ScreenErrorContainer from '../../common/error/ScreenErrorContainer';
import GoButton from '../../common/components/GoButton';
import StimulusButton from '../../common/stimulus/StimulusButton';
import { VOCAB } from '../../constants/measure';
import { localize, DICTIONARY_KEYS } from '../../common/localization';
import { ARIA_LABELS } from '../../constants/text';

export default class VocabBDView extends React.Component {
    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.narrateScreen();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.isDataFetched && this.narrationShouldStart(prevProps)) {
            this.props.narrateScreen(this.props.stimulus.audio);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    getStimulusArea() {
        if (this.props.isDataFetched) {
            return (
                <div>
                    <StimulusButton
                        isDisabled={this.props.isScreenGreyedOut}
                        styles="stimulus-top"
                        isPlaying={this.props.isStimulusPlaying}
                        onClick={this.playStimulus()}
                    />
                    <div
                        className="stimulus-text"
                        role="note"
                        aria-live="polite"
                        aria-label={this.props.stimulus.text}
                    >
                        <span
                            className="text-underlined"
                            role="note"
                            aria-label={this.props.stimulus.text}
                        >
                            {this.props.stimulus.text}
                        </span>
                    </div>
                </div>
            );
        }

        return null;
    }

    getResponsesArea() {
        if (this.props.isDataFetched) {
            return (
                <div className="vertical-answer-buttons">
                    {this.props.possibleResponses.map((response, index) => (
                        <AnswerButtonContainer
                            isDisabled={this.props.isScreenGreyedOut}
                            key={response.id}
                            index={index}
                            audio={response.audio}
                            text={response.text}
                            isSelected={index === this.props.selectedResponse}
                            onAnswerClick={this.handleSelectResponse(index)}
                        />
                    ))}
                </div>
            );
        }

        return null;
    }

    handleSelectResponse(response) {
        return () => {
            this.props.selectResponse(response);
        };
    }

    playStimulus() {
        return () => {
            this.props.playStimulus(this.props.stimulus.audio);
        };
    }

    narrationShouldStart(prevProps) {
        const isReady = this.props.isDataFetched;
        const shouldStartNarrate =
            prevProps.isDataFetched !== this.props.isDataFetched ||
            prevProps.questionIndex !== this.props.questionIndex;

        return isReady && shouldStartNarrate;
    }

    handleOnNext() {
        return () => {
            this.props.onNext(this.props.selectedResponse);
        };
    }

    render() {
        return (
            <div className={`${VOCAB.ASSESSMENT} ${VOCAB.BD}`}>
                <SplitLayout
                    upperElement={this.getStimulusArea()}
                    lowerElement={this.getResponsesArea()}
                    isScreenBlocked={this.props.isScreenBlocked}
                    isGreyedOut={this.props.isScreenGreyedOut}
                    questionIndex={this.props.questionIndex}
                    enableScroll
                >
                    <AudioContainer />
                    <ScreenErrorContainer onRetry={this.handleOnNext()} />
                    {this.props.isNextButtonActive && (
                        <GoButton
                            text={localize(DICTIONARY_KEYS.OK)}
                            onClick={this.handleOnNext()}
                            isEnabled={!this.props.isScreenBlocked}
                            ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                        />
                    )}
                </SplitLayout>
            </div>
        );
    }
}

VocabBDView.propTypes = {
    fetchContent: PropTypes.func,
    narrateScreen: PropTypes.func,
    onNext: PropTypes.func,
    cleanup: PropTypes.func,
    selectResponse: PropTypes.func,
    playStimulus: PropTypes.func,
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isScreenGreyedOut: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    isStimulusPlaying: PropTypes.bool,
    stimulus: PropTypes.object,
    possibleResponses: PropTypes.array,
    selectedResponse: PropTypes.number,
    questionIndex: PropTypes.number,
};

VocabBDView.defaultProps = {
    isDataFetched: false,
    isScreenBlocked: false,
    isScreenGreyedOut: false,
    isNextButtonActive: false,
    isStimulusPlaying: false,
    possibleResponses: [],
    stimulus: undefined,
    selectedResponse: undefined,
    questionIndex: undefined,
    fetchContent: () => {},
    selectResponse: () => {},
    playStimulus: () => {},
    narrateScreen: () => {},
    onNext: () => {},
    cleanup: () => {},
};
